import React, { useState, useEffect } from 'react';
import { getLeaks, getActionPlan } from '../../services/LossAnalysisService';
import { getSettings, updateSettings } from '../../services/SettingsService';
import { processError } from '../../services/ServiceUtils';
import HowItWorks from '../../components/HowItWorks/HowItWorks';

// "Análise de perdas": aponta o leak-detector pro próprio bot — símbolos que sangram de forma
// RECORRENTE (P&L líquido negativo com amostra suficiente) na janela/escopo da tela de Performance.
// Vai além do winRate agregado mostrando o que CORTAR. Read-only. Ver project_backlog_performance_diag.
function fmtMoney(v) {
    const n = Number(v) || 0;
    return `${n >= 0 ? '+' : ''}${n.toFixed(2)}`;
}

function MarketBadge({ isFuture }) {
    return isFuture
        ? <span className="badge bg-warning text-dark">Futuros</span>
        : <span className="badge bg-secondary">Spot</span>;
}

// Limiar de "recorrência": nº mínimo de operações fechadas pra um símbolo contar como vazamento.
const MIN_TRADES_OPTIONS = [2, 3, 5, 10];

// Rótulos amigáveis dos regimes de mercado (chave crua do beholder → humano).
const REGIME_LABELS = {
    TRENDING_UP: '📈 Tendência de alta',
    TRENDING_DOWN: '📉 Tendência de baixa',
    RANGING: '↔️ Lateral',
    HIGH_VOLATILITY: '⚡ Alta volatilidade',
    UNKNOWN: '❔ Sem histórico',
};
const regimeLabel = r => REGIME_LABELS[r] || r;

// Ícone por passo do plano de ação (espelha as orientações manuais da tela).
const STEP_ICON = { confirm: '🔎', diagnose: '🩺', act: '🛠️', remeasure: '🔁' };

function LossAnalysisPanel({ startDate, endDate, environment, market }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [minTrades, setMinTrades] = useState(3);
    const [plan, setPlan] = useState(null);
    const [digestOn, setDigestOn] = useState(false);
    const [autoPauseOn, setAutoPauseOn] = useState(false);

    useEffect(() => {
        getSettings().then(s => {
            setDigestOn(s && s.lossDigestEnabled === true);
            setAutoPauseOn(s && s.lossAutoPauseEnabled === true);
        }).catch(() => {});
    }, []);

    function onToggleDigest(e) {
        const on = e.target.checked;
        setDigestOn(on);
        updateSettings({ lossDigestEnabled: on }).catch(() => setDigestOn(!on)); // reverte no erro
    }

    function onToggleAutoPause(e) {
        const on = e.target.checked;
        setAutoPauseOn(on);
        updateSettings({ lossAutoPauseEnabled: on }).catch(() => setAutoPauseOn(!on));
    }

    useEffect(() => {
        if (!startDate || !endDate) return;
        setLoading(true);
        getLeaks(startDate, endDate, environment, market, minTrades)
            .then(d => { setData(d); setLoading(false); })
            .catch(err => { setError(processError(err)); setLoading(false); });
    }, [startDate, endDate, environment, market, minTrades]);

    // Plano de ação: só busca quando HÁ vazamento (evita chamada à toa). Automatiza as orientações manuais.
    const hasLeakers = !!(data && data.leakers && data.leakers.length);
    useEffect(() => {
        if (!hasLeakers) { setPlan(null); return; }
        getActionPlan(startDate, endDate, environment, market, minTrades)
            .then(setPlan)
            .catch(() => setPlan(null));
    }, [hasLeakers, startDate, endDate, environment, market, minTrades]);

    if (loading || error || !data) return null;

    const { leakers = [], winners = [], groups = 0, totalLeak = 0, byRegime = null, base = null, baseReason = '' } = data;
    // A RÉGUA fica escrita na tela. Até 11/08 estes números eram brutos e a coluna já dizia
    // "P&L líquido" (querendo dizer líquido do round-trip, não da taxa) — na produção isso mostrava
    // um símbolo de alto giro no positivo quando a corretagem já tinha comido o resultado. Hoje o
    // backend decide a régua pela cobertura da comissão na janela e manda qual foi; sem exibir
    // isso, o número volta a ser ambíguo.
    const liquido = base === 'liquido';

    // Sem ordens pareadas suficientes pra dizer algo: não polui a tela.
    if (!groups) return null;

    const topWinners = winners.slice(0, 3);

    return (
        <div className="card card-body border-0 shadow mt-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-1">
                <h5 className="mb-0">
                    📉 Análise de perdas recorrentes
                    {base && (
                        <span className={`badge ms-2 ${liquido ? 'bg-success' : 'bg-secondary'}`}
                            style={{ fontSize: '0.6em', verticalAlign: 'middle' }} title={baseReason}>
                            {liquido ? 'líquido de taxa' : 'bruto — taxa não medida'}
                        </span>
                    )}
                </h5>
                <div className="d-flex align-items-center gap-2">
                    <label className="text-muted mb-0" style={{ fontSize: '0.8em' }} htmlFor="lossMinTrades">Mín. de operações</label>
                    <select id="lossMinTrades" className="form-select form-select-sm" style={{ width: '4.75rem' }}
                        value={minTrades} onChange={e => setMinTrades(parseInt(e.target.value))}>
                        {MIN_TRADES_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                </div>
            </div>
            <p className="text-muted mb-2" style={{ fontSize: '0.85em' }}>
                Onde o bot <strong>sangra de forma recorrente</strong> — símbolos com prejuízo líquido e
                pelo menos {minTrades} operações fechadas na janela. Use pra decidir o que <strong>cortar ou revisar</strong>.
            </p>

            <div className="form-check form-switch mb-3">
                <input className="form-check-input" type="checkbox" id="lossDigestToggle"
                    checked={digestOn} onChange={onToggleDigest} />
                <label className="form-check-label" htmlFor="lossDigestToggle" style={{ fontSize: '0.85em' }}>
                    🔔 Resumo semanal por Telegram
                    <span className="text-muted d-block" style={{ fontSize: '0.9em' }}>
                        {digestOn
                            ? 'Ligado: aos domingos você recebe um alerta se houver vazamento crônico, com o plano de ação. Nada é executado.'
                            : 'Desligado: a análise só aparece quando você abre esta tela. Ligue para ser avisado sozinho.'}
                    </span>
                </label>
            </div>

            <div className="form-check form-switch mb-3">
                <input className="form-check-input" type="checkbox" id="lossAutoPauseToggle"
                    checked={autoPauseOn} onChange={onToggleAutoPause} />
                <label className="form-check-label" htmlFor="lossAutoPauseToggle" style={{ fontSize: '0.85em' }}>
                    🛑 Pausar sozinho os perdedores crônicos
                    <span className="text-muted d-block" style={{ fontSize: '0.9em' }}>
                        {autoPauseOn
                            ? 'Ligado: aos domingos o bot PAUSA as automações de símbolos com prejuízo crônico (no máximo 3 por semana) e te avisa. Só pausa (reversível a 1 clique nas Automações) — nunca vende, corta ou saca.'
                            : 'Desligado: nada é pausado sozinho — você decide pelo plano de ação. Ligue para o bot pausar os casos crônicos automaticamente (reversível).'}
                    </span>
                </label>
            </div>

            <HowItWorks title="Como funciona a análise?">
                <p className="mb-2">
                    Vai além do acerto médio: mostra <strong>onde</strong> o dinheiro vaza, por símbolo. É só um
                    diagnóstico (não mexe no bot) — quem decide a ação é você.
                </p>
                <p className="mb-1"><strong>Como usar</strong></p>
                <ul className="mb-2 ps-3">
                    <li>Escolha a <strong>janela de datas</strong> e o <strong>mercado</strong> (Spot/Futuros) no topo da tela — a análise os respeita.</li>
                    <li><strong>Mín. de operações</strong>: quantas operações fechadas um símbolo precisa ter pra contar como recorrente. 3 = padrão; 5–10 = só os crônicos; 2 = espiar cedo (mais ruído).</li>
                    <li>🟢 verde = sem vazamento recorrente. 🔴 vermelho = símbolos sangrando repetido, com o prejuízo somado.</li>
                </ul>
                <p className="mb-1"><strong>Se apontar um vazamento</strong></p>
                <ol className="mb-2 ps-3">
                    <li><strong>Confirme</strong>: suba o mín. pra 5 — se persistir, é crônico, não azar.</li>
                    <li><strong>Diagnostique</strong>: veja as ordens pareadas do símbolo, qual automação o opera, rode o "Simular comportamento" e confira a seção <strong>Por regime de mercado</strong> abaixo (o bot sangra quando compra em qual regime?).</li>
                    <li><strong>Aja</strong> (do leve ao pesado): pausar a automação do símbolo → apertar risco/stop/filtros → cortar o símbolo do foco → revisar a lógica (revisão por IA + re-simular).</li>
                    <li><strong>Re-meça</strong> na janela seguinte: o vazamento parou?</li>
                </ol>
                <p className="mb-0 text-warning">Não reaja a uma janela só — o alvo é sangria <strong>recorrente</strong> entre janelas (moeda nova/fase volátil gera vermelho passageiro).</p>
            </HowItWorks>

            {leakers.length === 0 ? (
                <div className="alert alert-success py-2 mb-0" style={{ fontSize: '0.9em' }}>
                    ✅ Nenhum vazamento recorrente detectado nesta janela ({groups} símbolo{groups > 1 ? 's' : ''} com operações fechadas).
                </div>
            ) : (
                <>
                    <div className="alert alert-danger py-2" style={{ fontSize: '0.9em' }}>
                        ⚠️ <strong>{leakers.length}</strong> símbolo{leakers.length > 1 ? 's sangram' : ' sangra'} recorrentemente —
                        prejuízo somado de <strong>{fmtMoney(totalLeak)}</strong> no período.
                    </div>
                    <div className="table-responsive">
                        <table className="table table-hover table-sm mb-0">
                            <thead>
                                <tr className="align-middle">
                                    <th>Símbolo</th>
                                    <th>Mercado</th>
                                    <th className="text-end">Operações</th>
                                    <th className="text-end">Acerto</th>
                                    <th className="text-end">{liquido ? 'P&L líquido de taxa' : 'P&L bruto'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leakers.map(r => (
                                    <tr key={r.key}>
                                        <td><strong>{r.symbol}</strong></td>
                                        <td><MarketBadge isFuture={r.isFuture} /></td>
                                        <td className="text-end">{r.trades} <span className="text-muted small">({r.wins}V/{r.losses}P)</span></td>
                                        <td className="text-end">{r.winRate}%</td>
                                        <td className="text-end text-danger fw-bold">{fmtMoney(r.net)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {plan && plan.plans && plan.plans.length > 0 && (
                <div className="mt-4">
                    <h6 className="mb-1">🧭 Plano de ação</h6>
                    <p className="text-muted mb-2" style={{ fontSize: '0.82em' }}>
                        As orientações da seção acima, já <strong>resolvidas por símbolo</strong>: confirmação de recorrência,
                        qual automação opera cada um e a ordem de ação sugerida. É só recomendação — <strong>nada é executado</strong>.
                    </p>
                    <div className="accordion" id="lossPlanAccordion">
                        {plan.plans.map((p, idx) => (
                            <div className="accordion-item" key={p.key}>
                                <h2 className="accordion-header">
                                    <button className={`accordion-button ${idx === 0 ? '' : 'collapsed'} py-2`} type="button"
                                        data-bs-toggle="collapse" data-bs-target={`#lossPlan${idx}`}>
                                        <span className="me-2"><strong>{p.symbol}</strong></span>
                                        {p.chronic
                                            ? <span className="badge bg-danger me-2">Crônico</span>
                                            : <span className="badge bg-warning text-dark me-2">A confirmar</span>}
                                        <span className="text-danger fw-bold me-2">{fmtMoney(p.net)}</span>
                                        <span className="text-muted small">{p.trades} ops · {p.winRate}%</span>
                                    </button>
                                </h2>
                                <div id={`lossPlan${idx}`} className={`accordion-collapse collapse ${idx === 0 ? 'show' : ''}`} data-bs-parent="#lossPlanAccordion">
                                    <div className="accordion-body py-2">
                                        <ol className="list-unstyled mb-0">
                                            {p.steps.map((s, i) => (
                                                <li key={i} className="mb-2">
                                                    <span className="me-1">{STEP_ICON[s.level] || '•'}</span>
                                                    <strong>{s.action}.</strong> <span className="text-muted">{s.detail}</span>
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {topWinners.length > 0 && (
                <div className="mt-3">
                    <small className="text-muted">
                        Em contraste, o que mais <strong>rende</strong>:{' '}
                        {topWinners.map((w, i) => (
                            <span key={w.key}>
                                {i > 0 && ' · '}
                                <strong>{w.symbol}</strong> <span className="text-success">{fmtMoney(w.net)}</span>
                            </span>
                        ))}
                    </small>
                </div>
            )}

            {byRegime && byRegime.length > 0 && (
                <div className="mt-4">
                    <h6 className="mb-1">Por regime de mercado</h6>
                    <p className="text-muted mb-2" style={{ fontSize: '0.82em' }}>
                        P&L somado pelo <strong>regime no momento da entrada</strong> — revela se o bot sangra
                        quando compra em tendência de baixa ou alta volatilidade, mesmo que o símbolo pareça neutro.
                    </p>
                    <div className="table-responsive">
                        <table className="table table-sm mb-0">
                            <thead>
                                <tr className="align-middle">
                                    <th>Regime na entrada</th>
                                    <th className="text-end">Operações</th>
                                    <th className="text-end">Acerto</th>
                                    <th className="text-end">{liquido ? 'P&L líquido de taxa' : 'P&L bruto'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {byRegime.map(r => (
                                    <tr key={r.regime}>
                                        <td>{regimeLabel(r.regime)}</td>
                                        <td className="text-end">{r.trades} <span className="text-muted small">({r.wins}V/{r.losses}P)</span></td>
                                        <td className="text-end">{r.winRate}%</td>
                                        <td className={`text-end fw-bold ${r.net < 0 ? 'text-danger' : 'text-success'}`}>{fmtMoney(r.net)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LossAnalysisPanel;
